import { LANG, tr } from '../../lib/i18n';
import {
  BUREAU,
  CHANTIER,
  DEPOT,
  joursCsv,
  libelleLieu,
  lieuHabituel,
  listeCsv,
} from '../../lib/lieux-travail';
import { nomCourtDuJour } from '../../lib/semaine';
import { C } from '../../theme/palette';
import { Etiquette, Puces } from './Puces.jsx';

const LIEUX = [BUREAU, DEPOT, CHANTIER];

/** Un lundi quelconque : ses six suivants donnent les noms des jours. */
const LUNDI = new Date(2024, 0, 1);

/** La lettre (ou l'abréviation) du jour ISO dans la langue de l'écran. */
function lettreDuJour(n) {
  const d = new Date(LUNDI);
  d.setDate(LUNDI.getDate() + n - 1);
  const nom = nomCourtDuJour(d, LANG);
  // En français, la lettre seule, comme la maquette : L M M J V S D.
  return String(LANG).startsWith('fr') ? nom.charAt(0).toUpperCase() : nom;
}

/**
 * Fiche de l'employé → « Où travaille-t-il ? » : son lieu habituel, les autres
 * lieux où il peut pointer, et le télétravail (non, tous les jours, certains
 * jours). Sans réglage, il pointe sur les chantiers comme toujours.
 *
 * @param {object} props propriétés
 * @param {object} props.f la fiche en cours de saisie
 * @param {(k: string, v: *) => void} props.set écrit un champ de la fiche
 * @returns {import('react').ReactElement} la section
 */
export function LieuxEmploye({ f, set }) {
  const habituel = lieuHabituel(f);
  const autres = listeCsv(f.lieuxAutres).filter((l) => l !== habituel);
  const teletravail = f.teletravail === 'TOUS' || f.teletravail === 'JOURS' ? f.teletravail : 'NON';
  const jours = joursCsv(f.teletravailJours);
  const bascule = (liste, v) => (liste.includes(v) ? liste.filter((x) => x !== v) : [...liste, v]);

  return (
    <div data-testid="lieux-employe">
      <Etiquette>{tr('Lieu habituel')}</Etiquette>
      <Puces
        titre={tr('Lieu habituel')}
        options={LIEUX.map((l) => [l, libelleLieu(l)])}
        valeur={habituel}
        onChange={(l) => {
          // Le chantier par défaut s'écrit vide : « comme aujourd'hui ».
          set('lieuHabituel', l === CHANTIER ? '' : l);
          set(
            'lieuxAutres',
            autres.filter((x) => x !== l),
          );
        }}
      />
      <Etiquette>{tr('Peut aussi pointer')}</Etiquette>
      <Puces
        titre={tr('Peut aussi pointer')}
        plusieurs
        options={LIEUX.filter((l) => l !== habituel).map((l) => [l, libelleLieu(l)])}
        valeur={autres}
        onChange={(l) => set('lieuxAutres', bascule(autres, l))}
      />
      <Etiquette>{tr('🏠 Télétravail')}</Etiquette>
      <Puces
        titre={tr('🏠 Télétravail')}
        options={[
          ['NON', tr('Non')],
          ['TOUS', tr('Tous les jours')],
          ['JOURS', tr('Certains jours')],
        ]}
        valeur={teletravail}
        onChange={(v) => set('teletravail', v === 'NON' ? '' : v)}
      />
      {teletravail === 'JOURS' && (
        <Puces
          titre={tr('Certains jours')}
          plusieurs
          petites
          couleur={C.purple}
          options={[1, 2, 3, 4, 5, 6, 7].map((n) => [n, lettreDuJour(n)])}
          valeur={jours}
          onChange={(n) =>
            set(
              'teletravailJours',
              bascule(jours, n).sort((a, b) => a - b),
            )
          }
        />
      )}
    </div>
  );
}
